import React from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { downscaleProportionally } from 'lib/scaleWidthHeight';
import { Icon, ICONS } from 'components/Icon';
import { theme } from 'lib/theme';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Image, ImageLoadEventData } from 'expo-image';

type Props = {
  style?: StyleProp<ViewStyle>;
  maxWidth: number;
  maxHeight: number;
  image: string;
  x: number;
  y: number;
};

export const MiniPlan: React.FC<Props> = ({
  style,
  image,
  maxWidth,
  maxHeight,
  x,
  y,
}) => {
  const pinSize = 21;

  const [loaded, setLoaded] = React.useState(false);

  const [dimensions, setDimensions] = React.useState<{
    sourceWidth: number;
    sourceHeight: number;
    width: number;
    height: number;
  }>({ sourceWidth: 0, sourceHeight: 0, width: 1, height: 1 });

  const handleLoaded = React.useCallback(
    (event: ImageLoadEventData) => {
      if (!loaded) {
        setDimensions({
          sourceWidth: event.source.width,
          sourceHeight: event.source.height,
          ...downscaleProportionally(
            event.source.width,
            event.source.height,
            maxWidth,
            maxHeight,
          ),
        });
        setLoaded(true);
      }
    },
    [loaded, maxHeight, maxWidth],
  );

  React.useEffect(() => {
    if (loaded) {
      setDimensions((existing) => ({
        sourceWidth: existing.sourceWidth,
        sourceHeight: existing.sourceHeight,
        ...downscaleProportionally(
          existing.sourceWidth,
          existing.sourceHeight,
          maxWidth,
          maxHeight,
        ),
      }));
    }
  }, [loaded, maxHeight, maxWidth]);

  return (
    <View style={style}>
      <Image
        source={{ uri: image }}
        onLoad={handleLoaded}
        style={{
          width: dimensions.width,
          height: dimensions.height,
        }}
      />
      {!loaded ? (
        <View
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 1,
          }}
        >
          <ActivityIndicator size={30} />
        </View>
      ) : (
        <View
          style={{
            width: pinSize,
            height: pinSize,
            position: 'absolute',
            left: dimensions.width * (x / 100) - pinSize / 2,
            top: dimensions.height * (y / 100) - pinSize,
          }}
        >
          <Icon
            source={ICONS.LocationPinSolidStroke}
            color={theme.colors.blue}
            size={pinSize}
          />
        </View>
      )}
    </View>
  );
};
